import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import React from 'react';

import { TagChipsProps } from './TagChips.props';
import { COLORS } from '../../../configs/colors';
import { TagCellItem } from '../TagCell';

/** Fallback palette for tags saved without a colour. */
const TAG_COLORS = [
  { bg: COLORS.TagBlueBg, text: COLORS.TagBlueText },
  { bg: COLORS.TagGreenBg, text: COLORS.TagGreenText },
  { bg: COLORS.TagAmberBg, text: COLORS.TagAmberText },
  { bg: COLORS.TagPurpleBg, text: COLORS.TagPurpleText },
  { bg: COLORS.TagRedBg, text: COLORS.TagRedText },
  { bg: COLORS.TagIndigoBg, text: COLORS.TagIndigoText },
];

/**
 * Picks black or white text for a tag's saved colour by WCAG contrast ratio.
 * The colour picker only offers dark swatches, but tags migrated from the
 * legacy system can carry any hex.
 */
export const readableTextColor = (hex: string): string => {
  const raw = hex.replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (!/^[0-9a-f]{6}$/i.test(full)) return '#ffffff';
  const channel = (offset: number) => {
    const value = parseInt(full.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  return contrastWithWhite >= contrastWithBlack ? '#ffffff' : '#000000';
};

const chipColors = (tag: TagCellItem, index: number) => {
  if (tag.color) return { bg: tag.color, text: readableTextColor(tag.color) };
  const fallback = TAG_COLORS[index % TAG_COLORS.length];
  return { bg: fallback.bg, text: fallback.text };
};

/**
 * The tag chips shown on the job detail header and in the jobs table. Sized to
 * sit inside a table row (10px against the row's 11px body text).
 */
export const TagChips: React.FC<TagChipsProps> = ({ tags, onTagClick }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
    {tags.map((tag, index) => {
      const colors = chipColors(tag, index);
      return (
        <Chip
          key={`${tag.title}-${index}`}
          label={tag.title}
          size="small"
          onClick={onTagClick}
          sx={{
            height: 16,
            fontSize: '10px',
            fontWeight: 600,
            bgcolor: colors.bg,
            color: colors.text,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      );
    })}
  </Box>
);
